import React from "react";
import { useTranslation } from "react-i18next";
import { Palette, Check } from "lucide-react";
import { Button } from "./button";
import { CardTitle } from "./card";
import { useTheme } from "../../contexts/ThemeContext";
import { cn } from "../../lib/utils";
import { Spinner } from "./spinner";

const ThemeSelector = () => {
  const { t } = useTranslation();
  const { currentTheme, themes, loading, switchTheme } = useTheme();

  const getThemePreview = (theme) => {
    return (
      <div className="w-full h-12 rounded-lg overflow-hidden flex shadow-sm">
        <div
          className="flex-[2]"
          style={{ backgroundColor: theme.colors.primary }}
        />
        <div
          className="flex-[3]"
          style={{ backgroundColor: theme.colors.secondary }}
        />
        <div
          className="flex-1"
          style={{ backgroundColor: theme.colors.accent }}
        />
      </div>
    );
  };

  return (
    <div className="w-full max-w-none space-y-4">
      <div>
        <CardTitle>
          {t("profile.themeSelection", "Theme Selection")}
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {t(
            "profile.themeDescription",
            "Choose from our collection of themes. Each theme creates a unique visual experience across your entire application.",
          )}
        </p>
      </div>

      <div className="space-y-6">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3">
              <Spinner className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                {t("profile.loadingThemes", "Loading themes...")}
              </span>
            </div>
          </div>
        )}

        {/* Theme Grid - Dynamic layout based on number of themes */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 w-full">
            {Object.entries(themes).map(([themeId, theme]) => (
              <div key={themeId} className="relative group">
                <Button
                  variant="ghost"
                  onClick={() => switchTheme(themeId)}
                  className={cn(
                    "premium-card h-auto p-3 w-full flex-col space-y-2 transition-all duration-300 relative overflow-hidden",
                    currentTheme === themeId
                      ? "border-2 border-primary shadow-premium-lg"
                      : "border-2 border-border/50 hover:border-primary/30 hover:shadow-premium-md",
                  )}
                >
                  {/* Theme Preview */}
                  <div className="w-full">{getThemePreview(theme)}</div>

                  {/* Theme Name */}
                  <div className="w-full text-center">
                    <h4 className="font-semibold text-xs leading-tight text-foreground">
                      {theme.name}
                    </h4>
                  </div>

                  {/* Active indicator */}
                  {currentTheme === themeId && (
                    <div className="absolute top-0 right-1 z-10">
                      <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    </div>
                  )}

                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemeSelector;
